/**
 * DriverQrCodeDialog.jsx
 * Dialog para gerar QR Code de configuracao do Driver App
 *
 * O QR Code contem:
 * {
 *   "type": "2ecomp_driver",
 *   "version": 1,
 *   "server": "https://server.example.com",
 *   "tenant": "TENANT_CODE"
 * }
 *
 * O motorista escaneia este QR no app para configurar automaticamente
 * o servidor e o codigo da empresa (tenant).
 */

import React, { useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  QrCode2 as QrIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import QRCode from 'react-qr-code';

import { useTranslation } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useSelector } from 'react-redux';

const DriverQrCodeDialog = ({
  open,
  onClose,
  driver,
  tenantCode,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const qrRef = useRef(null);

  // Get server URL from current window location
  const serverUrl = window.location.origin;

  // Get tenant code from Redux if not provided
  const revendaFromStore = useSelector((state) => state.session.revenda);
  const effectiveTenantCode = tenantCode || revendaFromStore?.code || '';

  // Generate QR Code payload following the PLANNING.md spec
  const qrPayload = useMemo(() => JSON.stringify({
    type: '2ecomp_driver',
    version: 1,
    server: serverUrl,
    tenant: effectiveTenantCode,
  }), [serverUrl, effectiveTenantCode]);

  // Copy QR data to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(qrPayload);
  };

  // Download QR as PNG
  const handleDownload = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const data = new XMLSerializer().serializeToString(svg);
    const img = new Image();

    canvas.width = 300;
    canvas.height = 300;

    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const link = document.createElement('a');
      link.download = `driver-qr-${driver?.name?.replace(/\s+/g, '-') || 'code'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };

    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(data)))}`;
  };

  // Print QR Code
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const svg = qrRef.current?.querySelector('svg');
    if (!printWindow || !svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - ${driver?.name || 'Driver'}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              font-family: Arial, sans-serif;
            }
            .container {
              text-align: center;
              padding: 40px;
            }
            .title {
              font-size: 24px;
              margin-bottom: 10px;
            }
            .subtitle {
              font-size: 14px;
              color: #666;
              margin-bottom: 30px;
            }
            .qr-code {
              margin: 20px 0;
            }
            .info {
              font-size: 12px;
              color: #888;
              margin-top: 20px;
            }
            .credentials {
              margin-top: 30px;
              padding: 20px;
              border: 1px dashed #ccc;
              border-radius: 8px;
              text-align: left;
            }
            .credentials h3 {
              margin: 0 0 10px 0;
              font-size: 14px;
            }
            .credentials p {
              margin: 5px 0;
              font-size: 13px;
            }
            @media print {
              body { -webkit-print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="title">${driver?.name || 'Motorista'}</div>
            <div class="subtitle">${t('driverQrCodeScanInstructions')}</div>
            <div class="qr-code">${svgData}</div>
            <div class="info">
              ${t('settingsServer')}: ${serverUrl}<br/>
              ${t('sharedTenant')}: ${effectiveTenantCode}
            </div>
            ${driver?.cpf ? `
              <div class="credentials">
                <h3>${t('driverAccountAccess')}</h3>
                <p><strong>Login (CPF):</strong> ${driver.cpf}</p>
                <p><strong>${t('userPassword')}:</strong> ${t('driverQrCodePasswordNote')}</p>
              </div>
            ` : ''}
          </div>
          <script>window.onload = function() { window.print(); window.close(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: tokens?.radius?.xl || '16px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: tokens?.colors?.surface?.elevated || '#f5f5f5',
          borderBottom: `1px solid ${tokens?.colors?.border?.subtle || '#e0e0e0'}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrIcon color="primary" />
          <Typography variant="h6">
            {t('driverQrCodeTitle')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Driver Info */}
        {driver && (
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              {driver.name}
            </Typography>
            {driver.cpf && (
              <Typography variant="body2" color="text.secondary">
                CPF: {driver.cpf}
              </Typography>
            )}
          </Box>
        )}

        {/* QR Code */}
        <Paper
          ref={qrRef}
          elevation={0}
          sx={{
            p: 3,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'white',
            borderRadius: tokens?.radius?.lg || '12px',
            border: `1px solid ${tokens?.colors?.border?.subtle || '#e0e0e0'}`,
          }}
        >
          <QRCode
            value={qrPayload}
            size={200}
            level="M"
            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
          />
        </Paper>

        {/* Instructions */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 2, textAlign: 'center' }}
        >
          {t('driverQrCodeInstructions')}
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Server Info */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              {t('settingsServer')}:
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {serverUrl}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              {t('sharedTenant')}:
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {effectiveTenantCode || '-'}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          bgcolor: tokens?.colors?.surface?.elevated || '#f5f5f5',
          borderTop: `1px solid ${tokens?.colors?.border?.subtle || '#e0e0e0'}`,
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('sharedCopy')}>
            <IconButton onClick={handleCopy} size="small">
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('reportExport')}>
            <IconButton onClick={handleDownload} size="small">
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('sharedPrint')}>
            <IconButton onClick={handlePrint} size="small">
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Button onClick={onClose} variant="contained">
          {t('sharedClose')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DriverQrCodeDialog;
